/**
 * Keyboard-focus tracker.
 *
 * Toggles `data-keyboard-focus="true"` on `<html>` only while the user is
 * navigating with the Tab key (or Shift+Tab). Any other interaction —
 * mouse click, touch, pointer, or any non-Tab key (Escape, Enter, arrows…)
 * — clears the flag.
 *
 * CSS in `index.css` gates focus outlines / rings on the attribute, so
 * focus indicators appear ONLY after Tab navigation. This is purely a
 * *visual* policy — we never call `.blur()`, so:
 *
 *   - Programmatic focus moves (dialogs, popovers, focus traps, autofocus
 *     after a click) keep their target focused; the ring just stays
 *     hidden until the user next presses Tab.
 *   - Form inputs, contenteditable, and screen-reader virtual cursors
 *     are unaffected — only the visual indicator is suppressed.
 *
 * Call `installKeyboardFocusTracker()` once at app startup.
 */
const ATTR = 'data-keyboard-focus';

let installed = false;

export function installKeyboardFocusTracker(): void {
  if (installed || typeof document === 'undefined') return;
  installed = true;

  const root = document.documentElement;

  const setKeyboard = () => {
    if (root.getAttribute(ATTR) !== 'true') root.setAttribute(ATTR, 'true');
  };
  const clearKeyboard = () => {
    if (root.hasAttribute(ATTR)) root.removeAttribute(ATTR);
  };

  // Tab (and Shift+Tab) is the ONLY input that opts focus rings in.
  // Every other key clears the flag so Escape / Enter / arrows after a
  // click cannot upgrade `:focus` to `:focus-visible`.
  window.addEventListener(
    'keydown',
    (ev) => {
      if (ev.key === 'Tab') setKeyboard();
      else clearKeyboard();
    },
    true,
  );

  // Any pointer interaction is "not keyboard navigation".
  // We do NOT blur — programmatic focus moves triggered by the click
  // (dialogs opening, popovers auto-focusing their first item) need the
  // focus to remain. The CSS rule keeps the ring hidden on its own.
  window.addEventListener('mousedown', clearKeyboard, true);
  window.addEventListener('pointerdown', clearKeyboard, true);
  window.addEventListener('touchstart', clearKeyboard, { capture: true, passive: true });
}
